import React from 'react'
import { useForm, Controller } from 'react-hook-form'
import dayjs from 'dayjs'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useCertificateMutations } from 'hooks/api'

import { Stack, Typography } from '@mui/material'

import { Button, Modal } from '@repo/ui'
import { DatePickerField } from 'components'

import type { CertificateAdmin } from 'types'

interface EditManufacturedAtFormValues {
  manufactured_at: string | null
}

interface EditManufacturedAtModalProps {
  open: boolean
  onClose: () => void
  certificate: CertificateAdmin | null
  refetch?: () => void
}

// VERA-924: minimal edit affordance for the per-item certificate
// manufacture date. Reuses the ReplaceTagModal Modal/Button skeleton and
// the OwnerDetailsFields DatePickerField+Controller pattern — no new form
// primitives, no new page (no Figma exists for a broader certificate edit
// surface).
export const EditManufacturedAtModal = (props: EditManufacturedAtModalProps) => {
  const { open, onClose, certificate, refetch } = props

  const { open: notify } = useSnackbar()

  const methods = useForm<EditManufacturedAtFormValues>({
    defaultValues: { manufactured_at: certificate?.manufactured_at ?? null },
  })
  const { control, handleSubmit, reset } = methods

  // DatePickerField only reads `defaultValue` at mount (own internal
  // uncontrolled state — see OwnerDetailsFields comment), so reset the form
  // whenever a different certificate is loaded into this modal.
  React.useEffect(() => {
    reset({ manufactured_at: certificate?.manufactured_at ?? null })
  }, [certificate?.id, certificate?.manufactured_at, reset])

  const [{ edit }, { isLoading }] = useCertificateMutations({
    onSuccess: () => {
      notify({ type: 'success', title: 'Manufacture date updated' })
      refetch?.()
      onClose()
    },
    onError: error => {
      notify({ type: 'error', title: getErrorMessage(error) })
    },
  })

  const onValid = (data: EditManufacturedAtFormValues) => {
    if (!certificate) return
    edit({
      certificateId: certificate.id,
      manufactured_at: data.manufactured_at,
    })
  }

  const handleClose = () => {
    if (isLoading) return
    onClose()
  }

  return (
    <Modal
      title="Edit manufacture date"
      open={open}
      onClose={handleClose}
      actions={
        <Stack direction="row" spacing={3}>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            disableElevation
            onClick={handleSubmit(onValid)}
            loading={isLoading}
            disabled={isLoading}
          >
            Save
          </Button>
        </Stack>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Set or correct the date this specific item was manufactured. This is
          independent of the product's manufacture date.
        </Typography>
        <Controller
          name="manufactured_at"
          control={control}
          render={({ field }) => (
            <DatePickerField
              label="Manufacture date"
              helperText="optional"
              name="manufactured_at"
              defaultValue={certificate?.manufactured_at ?? undefined}
              onValueChange={value => field.onChange(value)}
              format="MM/DD/YYYY"
              views={['year', 'month', 'day']}
              disableFuture
              maxDate={dayjs()}
              {...methods}
              {...field}
            />
          )}
        />
      </Stack>
    </Modal>
  )
}
